/**
 * StudioSidebar — Editorial floating sidebar for designers
 * Role-swapped replacement for AppSidebar when userType === "designer"
 * Matches spec exactly from designer_extracted_assets/unindexed/73f800ea-7e3b-4ec8-89c0-931649b09b73.javascript
 */

import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { UserMenu } from "@/components/layout/user-menu";
import { SkeletonRows } from "@/components/ui/skeleton";
import {
	getDesignerProjects,
	getDesignerRequests,
	type Project,
} from "@/features/projects/services/projects-api.service";
import { RoleUnreadDot } from "@/features/roles/components/role-unread-dot";
import { useDesignerProfile } from "@/features/settings/hooks/use-profile";
import { queryKeys } from "@/lib/query-keys";
import { Icn } from "../theme";
import { type StudioProject, useStudioStore } from "./studio-context";
import { STUDIO_NAV } from "./studio-nav-config";
import { useStudioNav } from "./use-studio-nav";

const ASSETS_URL = import.meta.env.VITE_ASSETS_URL as string;

// Map backend Project status to sidebar stage labels
function getStatusStage(status: string): string {
	const stageMap: Record<string, string> = {
		draft: "Draft",
		seeking_designer: "Available",
		awaiting_funding: "Funding",
		in_progress: "In Progress",
		completed: "Completed",
		cancelled: "Cancelled",
	};
	return stageMap[status] || status;
}

// Map backend Project data to StudioProject format
function mapProjectToStudioProject(project: Project): StudioProject {
	const img = project.projectImages?.[0] || project.beforeImages?.[0];
	return {
		id: project.id,
		name: project.title,
		loc: project.property?.locality || project.property?.city || "Location",
		stage: getStatusStage(project.status),
		img: img || `${ASSETS_URL}livingroom.webp`,
	};
}

interface StudioSidebarProps {
	collapsed: boolean;
	/** Omitted in the mobile menu sheet, which has no collapsed rail. */
	onToggleCollapse?: () => void;
	onOpenAssistant: () => void;
	/** Called on every navigation the sidebar makes. */
	onNavigate: () => void;
}

const iconBtnStyle = {
	width: 30,
	height: 30,
	border: 0,
	background: "transparent",
	borderRadius: 6,
	cursor: "pointer",
	color: "var(--color-dashboard-muted)",
	display: "flex" as const,
	alignItems: "center" as const,
	justifyContent: "center" as const,
	transition: "all 180ms ease",
};

export function StudioSidebar({
	collapsed,
	onToggleCollapse,
	onOpenAssistant,
	onNavigate,
}: StudioSidebarProps) {
	const { t } = useTranslation();
	const { activeProject, setActiveProject, requestCount, setRequestCount } =
		useStudioStore();
	const { nav } = useStudioNav();
	const location = useLocation();
	const activePath = location.pathname;
	const [switcherOpen, setSwitcherOpen] = useState(false);
	const switcherRef = useRef<HTMLDivElement>(null);
	const [hoveredProjectId, setHoveredProjectId] = useState<string | null>(null);
	const [isAllProjectsHovered, setIsAllProjectsHovered] = useState(false);
	const [hoveredNavPath, setHoveredNavPath] = useState<string | null>(null);
	const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());

	// Get designer profile for user-specific data
	const { data: designerProfile } = useDesignerProfile();

	// Queries rather than fetches in effects: on mobile this component mounts
	// each time the menu sheet opens, and a remount within the queries'
	// staleTime reads the cache instead of refetching.
	const projectsQuery = useQuery({
		queryKey: queryKeys.projects.list({ scope: "designer" }),
		queryFn: () => getDesignerProjects(),
	});
	const projectsLoading = projectsQuery.isPending;
	const projects = useMemo(
		() => (projectsQuery.data ?? []).map(mapProjectToStudioProject),
		[projectsQuery.data],
	);
	const requestsQuery = useQuery({
		queryKey: queryKeys.projects.designerRequests(),
		queryFn: () => getDesignerRequests(),
	});
	const designerRequests = requestsQuery.data;

	useEffect(() => {
		if (projectsQuery.error) {
			console.error("Failed to load designer projects:", projectsQuery.error);
		}
	}, [projectsQuery.error]);
	useEffect(() => {
		if (requestsQuery.error) {
			console.error("Failed to fetch request count:", requestsQuery.error);
		}
	}, [requestsQuery.error]);

	// Set first project as active if none is selected
	useEffect(() => {
		if (!activeProject && projects.length > 0) {
			setActiveProject(projects[0]);
		}
	}, [activeProject, projects, setActiveProject]);

	const navGroups = STUDIO_NAV;
	const curProject =
		projects.find((p) => p.id === activeProject?.id) ||
		(projects.length > 0 ? projects[0] : null);

	// Show loading state if no project is available
	const displayProject = curProject;

	// Helper function to get initials from name
	const getInitials = (name: string) => {
		if (!name) return "DN";
		return name
			.split(" ")
			.map((part) => part.charAt(0).toUpperCase())
			.join("")
			.slice(0, 2);
	};

	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (
				switcherRef.current &&
				!switcherRef.current.contains(e.target as Node)
			) {
				setSwitcherOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => document.removeEventListener("mousedown", handleClickOutside);
	}, []);

	useEffect(() => {
		if (designerRequests) {
			setRequestCount(designerRequests.length);
		}
	}, [designerRequests, setRequestCount]);

	const handleNav = (path: string, opts?: Parameters<typeof nav>[1]) => {
		onNavigate();
		nav(path, opts);
	};

	const handleProjectSelect = (project: StudioProject) => {
		setActiveProject(project);
		setSwitcherOpen(false);
		handleNav(`/projects/${project.id}`, { tab: "overview" });
	};

	const isActive = (id: string) =>
		activePath === `/${id}` || activePath.startsWith(`/${id}`);

	if (collapsed) {
		return (
			<aside
				style={{
					position: "fixed",
					top: 0,
					left: 0,
					bottom: 0,
					width: 80,
					padding: 14,
					zIndex: 60,
					display: "flex",
					flexDirection: "column",
				}}
			>
				<div
					style={{
						flex: 1,
						display: "flex",
						flexDirection: "column",
						background:
							"linear-gradient(180deg, var(--color-dashboard-surface) 0%, var(--color-dashboard-surface) 100%)",
						border: "1px solid var(--color-dashboard-border)",
						borderRadius: 15,
						boxShadow:
							"0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 4%, transparent), 0 24px 60px -22px color-mix(in srgb, var(--color-dashboard-shadow-ink) 28%, transparent), 0 8px 20px -12px color-mix(in srgb, var(--color-dashboard-shadow-ink) 14%, transparent)",
						overflow: "hidden",
					}}
				>
					{/* Brand mark - dark square */}
					<div
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							padding: "18px 0 14px",
						}}
					>
						<div
							style={{
								width: 36,
								height: 36,
								borderRadius: 8,
								background: "var(--color-dashboard-dark)",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<span
								style={{
									fontFamily: "var(--font-display)",
									fontWeight: 700,
									fontSize: 18,
									color: "var(--color-dashboard-on-fill)",
								}}
							>
								f<span style={{ color: "#FDBA74" }}>.</span>
							</span>
						</div>
					</div>

					{/* Expand button */}
					{onToggleCollapse && (
						<button
							type="button"
							onClick={onToggleCollapse}
							title="Expand"
							style={{ ...iconBtnStyle, alignSelf: "center", marginBottom: 8 }}
						>
							<Icn name="chevronRight" size={16} />
						</button>
					)}

					{/* Nav */}
					<nav
						style={{
							flex: 1,
							overflowY: "auto",
							padding: "4px 14px",
							display: "flex",
							flexDirection: "column",
							gap: 14,
						}}
					>
						{navGroups.map((g) => (
							<div key={g.label}>
								<div
									style={{
										height: 1,
										background: "var(--color-dashboard-border)",
										margin: "6px 8px 8px",
									}}
								/>
								<div
									style={{ display: "flex", flexDirection: "column", gap: 2 }}
								>
									{g.items.map((item) => {
										const active = isActive(item.path.replace("/", ""));
										return (
											<button
												type="button"
												key={item.path}
												onClick={() => handleNav(item.path)}
												title={item.label}
												style={{
													position: "relative",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													width: "100%",
													height: 42,
													padding: 0,
													borderRadius: 8,
													border: 0,
													cursor: "pointer",
													background: active
														? "linear-gradient(180deg, var(--color-dashboard-paper), var(--color-dashboard-divider))"
														: "transparent",
													transition: "background 200ms ease",
												}}
											>
												<span
													style={{
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
														width: 22,
														color: active
															? "var(--color-dashboard-accent-dark)"
															: "var(--color-dashboard-muted-dark)",
													}}
												>
													<item.icon size={20} />
												</span>
												{active && (
													<span
														style={{
															position: "absolute",
															top: 6,
															right: 8,
															width: 7,
															height: 7,
															borderRadius: "50%",
															background: "var(--color-dashboard-accent)",
															border:
																"1.5px solid var(--color-dashboard-paper)",
														}}
													/>
												)}
											</button>
										);
									})}
								</div>
							</div>
						))}
					</nav>

					{/* AI button - collapsed */}
					<div style={{ padding: "8px 14px" }}>
						<button
							type="button"
							onClick={onOpenAssistant}
							title="Open the command palette"
							style={{
								width: "100%",
								height: 48,
								borderRadius: 9,
								border: 0,
								cursor: "pointer",
								background:
									"linear-gradient(135deg, var(--color-dashboard-dark), var(--color-dashboard-dark-elevated))",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								boxShadow:
									"0 8px 20px -10px color-mix(in srgb, var(--color-dashboard-shadow-ink) 50%, transparent)",
							}}
						>
							<Icn name="sparkles" size={18} style={{ color: "#FDBA74" }} />
						</button>
					</div>

					{/* Profile footer - collapsed */}
					<div style={{ padding: 12 }}>
						<UserMenu name={designerProfile?.name} onNavigate={onNavigate}>
							<button
								type="button"
								title={designerProfile?.name || "Designer"}
								style={{
									width: "100%",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									padding: 0,
									border: 0,
									background: "transparent",
									cursor: "pointer",
									borderRadius: 8,
								}}
							>
								<div style={{ position: "relative" }}>
									{designerProfile?.profilePictureUrl ? (
										<img
											src={designerProfile.profilePictureUrl}
											alt={designerProfile.name}
											style={{
												width: 38,
												height: 38,
												borderRadius: "50%",
												objectFit: "cover",
											}}
										/>
									) : (
										<div
											style={{
												width: 38,
												height: 38,
												borderRadius: "50%",
												background: "var(--color-dashboard-dark)",
												color: "var(--color-dashboard-on-fill)",
												fontFamily: "var(--font-body)",
												fontSize: 14,
												fontWeight: 600,
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
											}}
										>
											{getInitials(designerProfile?.name || "")}
										</div>
									)}
									<span
										style={{
											position: "absolute",
											bottom: 0,
											right: 0,
											width: 11,
											height: 11,
											borderRadius: "50%",
											background: "var(--color-dashboard-success-fill)",
											border: "2px solid var(--color-dashboard-surface)",
										}}
									/>
									<RoleUnreadDot className="right-0 top-0" />
								</div>
							</button>
						</UserMenu>
					</div>
				</div>
			</aside>
		);
	}

	return (
		<aside
			style={{
				position: "fixed",
				top: 0,
				left: 0,
				bottom: 0,
				width: 268,
				padding: 14,
				zIndex: 60,
				transition: "width 380ms cubic-bezier(0.22,1,0.36,1)",
				display: "flex",
			}}
		>
			<div
				style={{
					flex: 1,
					display: "flex",
					flexDirection: "column",
					background:
						"linear-gradient(180deg, var(--color-dashboard-surface) 0%, var(--color-dashboard-surface) 100%)",
					border: "1px solid var(--color-dashboard-border)",
					borderRadius: 15,
					boxShadow:
						"0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 4%, transparent), 0 24px 60px -22px color-mix(in srgb, var(--color-dashboard-shadow-ink) 28%, transparent), 0 8px 20px -12px color-mix(in srgb, var(--color-dashboard-shadow-ink) 14%, transparent)",
					overflow: "hidden",
					position: "relative",
				}}
			>
				{/* Brand + collapse */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "18px 16px 14px",
					}}
				>
					<div style={{ display: "flex", alignItems: "baseline", gap: 2 }}>
						<span
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 700,
								fontSize: 24,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
							}}
						>
							finish
						</span>
						<span
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontWeight: 500,
								fontSize: 24,
								color: "var(--color-dashboard-accent-dark)",
							}}
						>
							d.
						</span>
					</div>
					{onToggleCollapse && (
						<button
							type="button"
							onClick={onToggleCollapse}
							title="Collapse"
							style={iconBtnStyle}
						>
							<Icn name="chevronLeft" size={16} />
						</button>
					)}
				</div>

				{/* Project switcher */}
				<div
					ref={switcherRef}
					style={{ padding: "0 14px 12px", position: "relative" }}
				>
					{displayProject ? (
						<button
							type="button"
							onClick={() => setSwitcherOpen((s) => !s)}
							style={{
								width: "100%",
								display: "flex",
								alignItems: "center",
								gap: 11,
								padding: "9px 11px",
								borderRadius: 9,
								cursor: "pointer",
								border: "1px solid var(--color-dashboard-border)",
								background: switcherOpen
									? "var(--color-dashboard-paper)"
									: "linear-gradient(180deg, var(--color-dashboard-paper), var(--color-dashboard-surface))",
								boxShadow:
									"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 70%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 5%, transparent)",
								transition: "all 200ms ease",
							}}
						>
							<div
								style={{
									width: 38,
									height: 38,
									borderRadius: 6,
									overflow: "hidden",
									flexShrink: 0,
									boxShadow:
										"0 1px 3px color-mix(in srgb, var(--color-dashboard-shadow-ink) 20%, transparent)",
								}}
							>
								{displayProject.img && !brokenImages.has(displayProject.img) ? (
									<img
										src={displayProject.img}
										alt=""
										onError={() => {
											const imgUrl = displayProject.img;
											if (imgUrl) {
												setBrokenImages((prev) => {
													const next = new Set(prev);
													next.add(imgUrl);
													return next;
												});
											}
										}}
										style={{
											width: "100%",
											height: "100%",
											objectFit: "cover",
										}}
									/>
								) : (
									<div
										style={{
											width: "100%",
											height: "100%",
											background:
												"linear-gradient(135deg, var(--color-dashboard-warm-200), var(--color-dashboard-warm-300))",
										}}
									/>
								)}
							</div>
							<div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
								<div
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 8.5,
										letterSpacing: "0.16em",
										color: "var(--color-dashboard-muted)",
										fontWeight: 600,
									}}
								>
									ACTIVE PROJECT
								</div>
								<div
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										fontSize: 14.5,
										fontWeight: 500,
										color: "var(--color-dashboard-text)",
										whiteSpace: "nowrap",
										overflow: "hidden",
										textOverflow: "ellipsis",
										marginTop: 1,
									}}
								>
									{displayProject.name}
								</div>
							</div>
							<Icn
								name="chevronDown"
								size={15}
								style={{
									color: "var(--color-dashboard-muted)",
									transform: switcherOpen ? "rotate(180deg)" : "none",
									transition: "transform 250ms ease",
								}}
							/>
						</button>
					) : projectsLoading ? (
						<div
							style={{
								padding: "9px 11px",
								borderRadius: 9,
								border: "1px solid var(--color-dashboard-border)",
								background:
									"linear-gradient(180deg, var(--color-dashboard-paper), var(--color-dashboard-surface))",
								boxShadow:
									"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 70%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 5%, transparent)",
							}}
						>
							<SkeletonRows
								label="Loading projects..."
								rows={1}
								rowClassName="h-4 bg-dashboard-border-soft"
							/>
						</div>
					) : (
						<div
							style={{
								padding: "9px 11px",
								borderRadius: 9,
								border: "1px solid var(--color-dashboard-border)",
								background:
									"linear-gradient(180deg, var(--color-dashboard-paper), var(--color-dashboard-surface))",
								boxShadow:
									"inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 70%, transparent), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 5%, transparent)",
							}}
						>
							<div
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 12,
									color: projectsQuery.isError
										? "var(--color-dashboard-accent-dark)"
										: "var(--color-dashboard-muted)",
									textAlign: "center",
								}}
							>
								{projectsQuery.isError
									? t("designerDashboard.sidebarProjectsLoadError")
									: "No active projects"}
							</div>
						</div>
					)}

					{switcherOpen && projects.length > 0 && (
						<div
							style={{
								position: "absolute",
								left: 14,
								right: 14,
								top: 64,
								zIndex: 80,
								background: "var(--color-dashboard-paper)",
								border: "1px solid var(--color-dashboard-border)",
								borderRadius: 11,
								boxShadow:
									"0 16px 40px -10px color-mix(in srgb, var(--color-dashboard-shadow-ink) 30%, transparent), 0 4px 8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 8%, transparent)",
								padding: 8,
								maxHeight: 400,
								overflowY: "auto",
							}}
						>
							<div style={{ padding: "6px 10px 8px" }}>
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 8.5,
										letterSpacing: "0.16em",
										color: "var(--color-dashboard-muted)",
										fontWeight: 600,
										margin: 0,
									}}
								>
									MY PROJECTS · {projects.length}
								</p>
							</div>
							{projects.map((p) => {
								const on = p.id === curProject?.id;
								return (
									<button
										type="button"
										key={p.id}
										onClick={() => handleProjectSelect(p)}
										style={{
											width: "100%",
											display: "flex",
											alignItems: "center",
											gap: 11,
											padding: 8,
											borderRadius: 8,
											border: 0,
											cursor: "pointer",
											background:
												on || p.id === hoveredProjectId
													? "var(--color-dashboard-divider)"
													: "transparent",
											transition: "background 150ms ease",
										}}
										onMouseEnter={() => setHoveredProjectId(p.id)}
										onMouseLeave={() => setHoveredProjectId(null)}
									>
										<div
											style={{
												width: 38,
												height: 38,
												borderRadius: 6,
												overflow: "hidden",
												flexShrink: 0,
											}}
										>
											{p.img && !brokenImages.has(p.img) ? (
												<img
													src={p.img}
													alt=""
													onError={() => {
														const imgUrl = p.img;
														if (imgUrl) {
															setBrokenImages((prev) => {
																const next = new Set(prev);
																next.add(imgUrl);
																return next;
															});
														}
													}}
													style={{
														width: "100%",
														height: "100%",
														objectFit: "cover",
													}}
												/>
											) : (
												<div
													style={{
														width: "100%",
														height: "100%",
														background:
															"linear-gradient(135deg, var(--color-dashboard-warm-200), var(--color-dashboard-warm-300))",
													}}
												/>
											)}
										</div>
										<div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
											<div
												style={{
													fontFamily: "var(--font-body)",
													fontWeight: 600,
													fontSize: 13,
													color: "var(--color-dashboard-text)",
													whiteSpace: "nowrap",
													overflow: "hidden",
													textOverflow: "ellipsis",
												}}
											>
												{p.name}
											</div>
											<div
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 9,
													letterSpacing: "0.1em",
													color: "var(--color-dashboard-muted)",
													marginTop: 2,
													textTransform: "uppercase",
												}}
											>
												{p.loc} · {p.stage}
											</div>
										</div>
										{on && (
											<Icn
												name="check"
												size={15}
												style={{ color: "var(--color-dashboard-accent-dark)" }}
											/>
										)}
									</button>
								);
							})}
							<div
								style={{
									borderTop: "1px solid var(--color-dashboard-border)",
									marginTop: 6,
									paddingTop: 6,
								}}
							>
								<button
									type="button"
									onClick={() => {
										setSwitcherOpen(false);
										handleNav("/projects");
									}}
									style={{
										width: "100%",
										display: "flex",
										alignItems: "center",
										gap: 10,
										padding: "9px 10px",
										borderRadius: 8,
										border: 0,
										cursor: "pointer",
										background: isAllProjectsHovered
											? "color-mix(in srgb, var(--color-dashboard-accent) 6%, transparent)"
											: "transparent",
										fontFamily: "var(--font-body)",
										fontSize: 12.5,
										fontWeight: 600,
										color: "var(--color-dashboard-accent-dark)",
									}}
									onMouseEnter={() => setIsAllProjectsHovered(true)}
									onMouseLeave={() => setIsAllProjectsHovered(false)}
								>
									<Icn name="grid" size={16} /> All projects
								</button>
							</div>
						</div>
					)}
				</div>

				{/* Nav groups */}
				<nav
					style={{
						flex: 1,
						overflowY: "auto",
						padding: "4px 18px",
						display: "flex",
						flexDirection: "column",
						gap: 14,
					}}
				>
					{navGroups.map((g) => (
						<div key={g.label}>
							<div
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 8.5,
									letterSpacing: "0.2em",
									color: "var(--color-dashboard-placeholder)",
									fontWeight: 600,
									padding: "4px 4px 7px",
								}}
							>
								{g.label}
							</div>
							<div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
								{g.items.map((item) => {
									const active = isActive(item.path.replace("/", ""));
									return (
										<button
											type="button"
											key={item.path}
											onClick={() => handleNav(item.path)}
											style={{
												position: "relative",
												display: "flex",
												alignItems: "center",
												gap: 12,
												width: "100%",
												height: 42,
												padding: "0 12px",
												borderRadius: 8,
												border: 0,
												cursor: "pointer",
												background: active
													? "linear-gradient(180deg, var(--color-dashboard-paper), var(--color-dashboard-divider))"
													: item.path === hoveredNavPath
														? "color-mix(in srgb, var(--color-dashboard-accent) 5%, transparent)"
														: "transparent",
												boxShadow: active
													? "inset 0 0 0 1px var(--color-dashboard-border), 0 1px 2px color-mix(in srgb, var(--color-dashboard-shadow-ink) 5%, transparent), 0 6px 16px -8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 18%, transparent)"
													: "none",
												transition:
													"background 200ms ease, box-shadow 250ms cubic-bezier(0.22,1,0.36,1)",
												fontFamily: "var(--font-body)",
											}}
											onMouseEnter={() => setHoveredNavPath(item.path)}
											onMouseLeave={() => setHoveredNavPath(null)}
										>
											{active && (
												<span
													style={{
														position: "absolute",
														left: -10,
														top: "50%",
														transform: "translateY(-50%)",
														width: 3,
														height: 18,
														borderRadius: 69,
														background: "var(--color-dashboard-accent)",
													}}
												/>
											)}
											<span
												style={{
													position: "relative",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													width: 22,
													color: active
														? "var(--color-dashboard-accent-dark)"
														: "var(--color-dashboard-muted-dark)",
												}}
											>
												<item.icon size={20} />
											</span>
											<span
												style={{
													flex: 1,
													textAlign: "left",
													fontFamily: active
														? "var(--font-display)"
														: "var(--font-body)",
													fontStyle: active ? "italic" : "normal",
													fontSize: active ? 16 : 13.5,
													fontWeight: 500,
													letterSpacing: active ? "-0.005em" : "0.005em",
													color: active
														? "var(--color-dashboard-text)"
														: "var(--color-dashboard-muted-dark)",
												}}
											>
												{item.label}
											</span>
											{/* A count only from a read that succeeded: while the
												read is pending or has failed, the store holds the last
												figure it was given, which may be stale. */}
											{requestsQuery.isSuccess &&
											requestCount > 0 &&
											item.path === "/requests" ? (
												<span
													style={{
														minWidth: 19,
														height: 19,
														padding: "0 5px",
														borderRadius: 69,
														background: "var(--color-dashboard-border)",
														color: "var(--color-dashboard-muted-dark)",
														fontFamily: "var(--font-body)",
														fontSize: 10.5,
														fontWeight: 700,
														display: "flex",
														alignItems: "center",
														justifyContent: "center",
													}}
												>
													{requestCount}
												</span>
											) : null}
										</button>
									);
								})}
							</div>
						</div>
					))}
				</nav>

				{/* AI assistant shortcut */}
				<div style={{ padding: "8px 18px 0" }}>
					<button
						type="button"
						onClick={onOpenAssistant}
						style={{
							width: "100%",
							textAlign: "left",
							cursor: "pointer",
							border: 0,
							borderRadius: 10,
							padding: "12px 14px",
							background:
								"linear-gradient(135deg, var(--color-dashboard-dark) 0%, var(--color-dashboard-dark-elevated) 100%)",
							boxShadow:
								"0 8px 20px -10px color-mix(in srgb, var(--color-dashboard-shadow-ink) 50%, transparent), inset 0 1px 0 rgba(253,186,116,0.15)",
							display: "flex",
							alignItems: "center",
							gap: 11,
							position: "relative",
							overflow: "hidden",
						}}
					>
						<div
							style={{
								position: "absolute",
								top: -20,
								right: -10,
								width: 80,
								height: 80,
								borderRadius: "50%",
								background:
									"radial-gradient(circle, rgba(253,186,116,0.22), transparent 70%)",
							}}
						/>
						<div
							style={{
								width: 34,
								height: 34,
								borderRadius: 7,
								background:
									"linear-gradient(135deg, var(--color-dashboard-orange-300), var(--color-dashboard-accent))",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
								boxShadow:
									"0 2px 8px color-mix(in srgb, var(--color-dashboard-accent) 50%, transparent)",
							}}
						>
							<Icn
								name="sparkles"
								size={17}
								style={{ color: "var(--color-dashboard-on-fill)" }}
							/>
						</div>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div
								style={{
									fontFamily: "var(--font-display)",
									fontStyle: "italic",
									fontSize: 14,
									fontWeight: 500,
									color: "var(--color-dashboard-on-fill)",
								}}
							>
								Command palette
							</div>
							<div
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 8.5,
									letterSpacing: "0.12em",
									color:
										"color-mix(in srgb, var(--color-dashboard-on-photo-warm) 65%, transparent)",
									marginTop: 2,
								}}
							>
								⌘K · ANYTHING
							</div>
						</div>
					</button>
				</div>

				{/* Profile footer */}
				<div style={{ padding: "12px 18px 16px", marginTop: 8 }}>
					<div
						style={{
							height: 1,
							background: "var(--color-dashboard-border)",
							marginBottom: 12,
						}}
					/>
					<UserMenu name={designerProfile?.name} onNavigate={onNavigate}>
						<button
							type="button"
							style={{
								width: "100%",
								display: "flex",
								alignItems: "center",
								gap: 11,
								padding: 4,
								border: 0,
								background: "transparent",
								cursor: "pointer",
								borderRadius: 8,
							}}
						>
							<div style={{ position: "relative" }}>
								{designerProfile?.profilePictureUrl ? (
									<img
										src={designerProfile.profilePictureUrl}
										alt={designerProfile.name}
										style={{
											width: 38,
											height: 38,
											borderRadius: "50%",
											objectFit: "cover",
										}}
									/>
								) : (
									<div
										style={{
											width: 38,
											height: 38,
											borderRadius: "50%",
											background: "var(--color-dashboard-dark)",
											color: "var(--color-dashboard-on-fill)",
											fontFamily: "var(--font-body)",
											fontSize: 14,
											fontWeight: 600,
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										{getInitials(designerProfile?.name || "")}
									</div>
								)}
								<span
									style={{
										position: "absolute",
										bottom: 0,
										right: 0,
										width: 11,
										height: 11,
										borderRadius: "50%",
										background: "var(--color-dashboard-success-fill)",
										border: "2px solid var(--color-dashboard-surface)",
									}}
								/>
								<RoleUnreadDot className="right-0 top-0" />
							</div>
							<div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
								<div
									style={{
										fontFamily: "var(--font-display)",
										fontStyle: "italic",
										fontSize: 14.5,
										fontWeight: 500,
										color: "var(--color-dashboard-text)",
									}}
								>
									{designerProfile?.name || "Designer"}
								</div>
								<div
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 8.5,
										letterSpacing: "0.14em",
										color: "var(--color-dashboard-muted)",
										marginTop: 1,
									}}
								>
									{designerProfile?.firmName || "DESIGNER STUDIO"}
								</div>
							</div>
						</button>
					</UserMenu>
				</div>
			</div>
		</aside>
	);
}
